/**
 * Club Manager
 * 
 * Provides focused club management with consistent patterns and enhanced performance
 */

import type { Club, Space } from '@/lib/types';
import { format } from 'date-fns';
import { generateNumericId } from '@/lib/utils';
import { isSampleDataMode } from '@/lib/config/env';
// Sample data is now imported dynamically to avoid build issues in production

// Private data storage
let clubs: Club[] = [];
let cache: Club[] | null = null;
let lastCacheTime = 0;
const CACHE_DURATION = 5 * 60 * 1000; // 5 minutes

type CreateClubData = Omit<Club, 'id' | 'dateCreated' | 'dateUpdated'>;
type UpdateClubData = Partial<Omit<Club, 'id' | 'dateCreated' | 'dateUpdated'>>;

export interface ClubFilters {
  organizationId?: string;
  search?: string;
  isActive?: boolean;
}

/**
 * Initialize clubs data from sample data
 */
const initializeData = async (): Promise<void> => {
  if (clubs.length === 0) {
    try {
      // Only import sample data if we're in sample data mode
      if (isSampleDataMode()) {
        const sampleDataModule = await import('@/lib/sample-data');
        clubs = sampleDataModule.sampleClubsData ? JSON.parse(JSON.stringify(sampleDataModule.sampleClubsData)) : [];
      }
    } catch (importError) {
      console.warn('Sample clubs data not available:', importError);
      clubs = [];
    }
  }
};

// Initialize on module load
initializeData().catch(error => {
  console.error('Failed to initialize clubs data:', error);
  clubs = [];
});

/**
 * Check if cache is still valid
 */
const isCacheValid = (): boolean => {
  return cache !== null && (Date.now() - lastCacheTime) < CACHE_DURATION;
};

const getCachedData = async (): Promise<Club[]> => {
  // Initialize data asynchronously if needed
  if (clubs.length === 0) {
    await initializeData();
  }
  
  if (!isCacheValid()) {
    cache = JSON.parse(JSON.stringify(clubs));
    lastCacheTime = Date.now();
  }
  
  return cache || [];
};

class ClubManager {
  /**
   * Get all clubs with optional filtering
   */
  async getAllClubs(filters?: ClubFilters): Promise<Club[]> {
    let clubsData = await getCachedData();

    if (filters) {
      if (filters.organizationId) {
        clubsData = clubsData.filter(club => club.organizationId === filters.organizationId);
      }
      
      if (filters.search) {
        const searchLower = filters.search.toLowerCase();
        clubsData = clubsData.filter(club => 
          club.name.toLowerCase().includes(searchLower) ||
          club.description?.toLowerCase().includes(searchLower)
        );
      }
      
      if (typeof filters.isActive === 'boolean') {
        clubsData = clubsData.filter(club => 
          filters.isActive ? club.status === 'Active' : club.status !== 'Active'
        );
      }
    }

    return clubsData;
  }

  /**
   * Get clubs by organization
   */
  async getClubsByOrganization(organizationId: string): Promise<Club[]> {
    return this.getAllClubs({ organizationId });
  }

  /**
   * Get club by ID
   */
  async getClubById(id: string): Promise<Club | null> {
    const clubsData = await getCachedData();
    return clubsData.find(club => club.id === id) || null;
  }

  /**
   * Create new club
   */
  async createClub(data: CreateClubData): Promise<Club> {
    const now = format(new Date(), 'yyyy-MM-dd HH:mm:ss');
    
    const newClub: Club = {
      ...data,
      id: generateNumericId(10),
      dateCreated: now,
      dateUpdated: now,
    };

    clubs.push(newClub);
    cache = null; // Invalidate cache
    
    return JSON.parse(JSON.stringify(newClub));
  }

  /**
   * Update existing club
   */
  async updateClub(id: string, updates: UpdateClubData): Promise<Club | null> {
    const clubIndex = clubs.findIndex(club => club.id === id);
    
    if (clubIndex === -1) {
      return null;
    }

    // Handle address updates separately to maintain structure
    if (updates.address) {
      clubs[clubIndex].address = {
        ...(clubs[clubIndex].address || {}),
        ...updates.address,
      };
      delete updates.address;
    }

    clubs[clubIndex] = {
      ...clubs[clubIndex],
      ...updates,
      dateUpdated: format(new Date(), 'yyyy-MM-dd HH:mm:ss'),
    };

    cache = null; // Invalidate cache
    
    return JSON.parse(JSON.stringify(clubs[clubIndex]));
  }

  /**
   * Delete club
   */
  async deleteClub(id: string): Promise<boolean> {
    const initialLength = clubs.length;
    clubs = clubs.filter(club => club.id !== id);
    
    if (clubs.length < initialLength) {
      cache = null; // Invalidate cache
      return true;
    }
    
    return false;
  }

  /**
   * Get spaces for a specific club
   */
  async getSpacesForClub(clubId: string): Promise<Space[]> {
    // Import spaceManager dynamically to avoid circular dependency
    const { spaceManager } = await import('./spaceManager');
    return spaceManager.getSpacesByClub(clubId);
  }

  /**
   * Get space count for a specific club
   */
  async getSpaceCountForClub(clubId: string): Promise<number> {
    const spaces = await this.getSpacesForClub(clubId);
    return spaces.length;
  }

  /**
   * Get club with space count
   */
  async getClubWithSpaceCount(clubId: string): Promise<(Club & { spaceCount: number }) | null> {
    const club = await this.getClubById(clubId);
    if (!club) return null;
    
    const spaceCount = await this.getSpaceCountForClub(clubId);
    return { ...club, spaceCount };
  }

  /**
   * Get all clubs with space counts
   */
  async getAllClubsWithSpaceCounts(filters?: ClubFilters): Promise<(Club & { spaceCount: number })[]> {
    const clubsData = await this.getAllClubs(filters);
    
    const clubsWithCounts = await Promise.all(
      clubsData.map(async (club) => {
        const spaceCount = await this.getSpaceCountForClub(club.id);
        return { ...club, spaceCount };
      })
    );
    
    return clubsWithCounts;
  }

  /**
   * Validate club data
   */
  async validateClubData(data: CreateClubData | UpdateClubData): Promise<{
    isValid: boolean;
    errors: string[];
  }> {
    const errors: string[] = [];

    // Required field validation for new clubs
    if ('name' in data && !data.name?.trim()) {
      errors.push('Club name is required');
    }

    if ('organizationId' in data && !data.organizationId?.trim()) {
      errors.push('Organization ID is required');
    }

    // Email validation if provided
    if (data.contactEmail) {
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      if (!emailRegex.test(data.contactEmail)) {
        errors.push('Invalid email format');
      }
    }

    // Phone validation if provided
    if (data.contactPhone) {
      // Simple phone validation - just check for reasonable length and characters
      const phoneRegex = /^[\+]?[\d\s\-\(\)]{7,15}$/;
      if (!phoneRegex.test(data.contactPhone)) {
        errors.push('Invalid phone format');
      }
    }

    return {
      isValid: errors.length === 0,
      errors
    };
  }

  /**
   * Search clubs across multiple fields
   */
  async searchClubs(query: string, organizationId?: string): Promise<Club[]> {
    const searchLower = query.toLowerCase();
    
    return this.getAllClubs({
      organizationId,
      search: searchLower
    });
  }

  /**
   * Get club statistics
   */
  async getClubStats(): Promise<{
    total: number;
    active: number;
    inactive: number;
    byOrganization: Record<string, number>;
  }> {
    const clubsData = await getCachedData();
    
    const stats = {
      total: clubsData.length,
      active: clubsData.filter(c => c.status === 'Active').length,
      inactive: clubsData.filter(c => c.status !== 'Active').length,
      byOrganization: {} as Record<string, number>
    };

    // Group by organization
    clubsData.forEach(club => {
      stats.byOrganization[club.organizationId] = (stats.byOrganization[club.organizationId] || 0) + 1;
    });

    return stats;
  }
}

export const clubManager = new ClubManager();
export default clubManager; 
